import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import BackendService from '../services/backendService';
import { FileUploads } from '../services/backendService/types';
import {
  PassageAttachment,
  useUploadPassageAttachment,
} from './useUploadMaterialAttachment';

/** What /processDocument needs to open a file the backend already holds. */
export type ParsableFileUpload = Pick<
  FileUploads,
  'id' | 'tenantId' | 'fileId' | 'mimeType'
>;

/**
 * Reads a file the teacher picked and hands back its text.
 *
 * The generator's passage input travels as `{ value, fileType }` — a file path
 * plus its extension when a file was uploaded, or the raw passage text when it
 * was pasted. Only pdf/jpeg/png survive the file-path route, so anything else a
 * teacher has their passage in (a .docx, a Word doc, an .html export, a
 * screenshot) had nowhere to go.
 *
 * This takes the pasted-text route instead: the file goes through the material
 * attachment pipeline, which runs /processDocument to turn it into markdown,
 * and the markdown comes straight back down into the textarea. The generator
 * then sees text it already knows how to read, and the teacher sees — and can
 * fix — exactly what we extracted before the lesson is generated.
 */
export function useParsePassageFile(tenantId: string) {
  const { uploadAsync } = useUploadPassageAttachment(tenantId);

  const { mutate: parseFile, isPending: isParsing, isError, error, reset } = useMutation({
    mutationFn: async ({
      file,
    }: {
      file: File;
    }): Promise<{
      text: string;
      fileName: string;
      /**
       * The file the text was read out of — the markdown /processDocument
       * produced. A caller that shows the text for confirmation keeps this, so
       * a passage the teacher didn't touch can be reused as it is rather than
       * uploaded a second time.
       */
      attachment: PassageAttachment;
    }> => {
      const { materialAttachment } = await uploadAsync({
        file,
        attachmentMethod: file.type === 'text/plain' ? 'paste' : 'upload',
      });

      const text = await fetchUploadedText(materialAttachment.fileId);

      if (!text.trim()) {
        throw new Error('Failed to find content in parsed file');
      }

      return { text, fileName: file.name, attachment: materialAttachment };
    },
  });

  return { parseFile, isParsing, isError, error, reset };
}

/**
 * The same parse for a file the backend already copied into the bucket —
 * a Google Drive pick, which never passes through the browser. The copy hands
 * back the FileUpload id, the bucket path and the mime type, which is all
 * /processDocument reads.
 */
export function useParsePassageFileUpload() {
  const {
    mutate: parseFileUpload,
    isPending: isParsing,
    isError,
    error,
    reset,
  } = useMutation({
    mutationFn: async ({
      fileUpload,
      fileName,
    }: {
      fileUpload: ParsableFileUpload;
      fileName: string;
    }): Promise<{ text: string; fileName: string }> => {
      const {
        fileUpload: processed,
      } = await BackendService.instance.materials.processDocument({
        fileUpload,
        isPassage: true,
      });

      const text = await fetchUploadedText(processed.fileId);

      if (!text.trim()) {
        throw new Error('Failed to find content in parsed file');
      }

      return { text, fileName };
    },
  });

  return { parseFileUpload, isParsing, isError, error, reset };
}

/**
 * The processed file's contents. A `.txt` skips /processDocument and comes back
 * as itself; everything else comes back as the markdown docling produced. Both
 * are plain text behind a signed download url.
 */
async function fetchUploadedText(fileId: string): Promise<string> {
  const urlResponse = await BackendService.instance.getDownloadUrl(fileId);
  const response = await axios.request<string>({
    method: 'get',
    url: urlResponse.data.url,
    responseType: 'text',
    // A passage that happens to open with a number or a quote would otherwise
    // be JSON-parsed out from under us.
    transformResponse: raw => raw,
  });
  return response.data ?? '';
}
